/* Mediability: a modern, digital proposal letter with a product inside. Big type, calm surfaces, very few elements. */
import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { KvitebergLogo } from "../KvitebergLogo";
import { fmtDate, monthLabel, useKeys, useMeta, useReveal } from "./proposalUtils";
import { downloadProposalPdf } from "../../pdf/download";
import { MbStage, MbStill } from "./MbApp";
import { PhoneStill } from "./PhoneStill";
import { LiveSystem } from "./LiveSystem";
import { QuoteShowcase } from "./QuoteShowcase";
import { SystemMockup } from "./SystemMockup";
import { WorkspaceMockup } from "./WorkspaceMockup";

/* >= 700 px: the real system is previewed inline under the letter. Below that, one button opens it full screen. */
const useWide = () => {
  const [wide, setWide] = useState(() => (typeof window !== "undefined" ? window.matchMedia("(min-width: 700px)").matches : true));
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 700px)");
    const onChange = (e) => setWide(e.matches);
    setWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return wide;
};

const usePdf = (d) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await downloadProposalPdf(d);
    } catch (e) {
      console.error("PDF", e);
      window.print();
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
};

const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrolled;
};

/* top bar: the logo, the language, the PDF – nothing else */
const Top = ({ d }) => {
  const [busy, onPdf] = usePdf(d);
  const scrolled = useScrolled();
  const lt = d.langToggle;
  return (
    <div className={`kvl-top ${scrolled ? "is-scrolled" : ""}`} data-testid="tilbod-topbar">
      <div className="kvl-wide kvl-top-in">
        <a href="/" className="kvl-logo" aria-label="Kviteberg">
          <KvitebergLogo className="kvl-logo-svg" />
        </a>
        <div className="kvl-top-actions">
          {lt && (
            <div className="kvl-lang" role="group" aria-label="Language" data-testid="tilbod-lang-toggle">
              {lt.options.map(([code, label]) => (
                <button key={code} type="button" lang={code === "no" ? "nb" : code} className={code === lt.current ? "is-on" : ""} aria-pressed={code === lt.current} aria-label={label} onClick={() => lt.set(code)} data-testid={`tilbod-lang-${code}`}>
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          )}
          <button type="button" className="kvl-btn kvl-btn-sm" onClick={onPdf} disabled={busy} aria-busy={busy} data-testid="tilbod-print-btn">
            {busy ? d.ui.pdfBusy : d.ui.pdf}
          </button>
        </div>
      </div>
    </div>
  );
};

const Lines = ({ text, as: Tag = "h2", className, testid }) => (
  <Tag className={className} data-testid={testid}>
    {text.split("\n").map((l, i) => (
      <span key={l} className="kvl-line">
        {l}
        {i < text.split("\n").length - 1 && <br />}
      </span>
    ))}
  </Tag>
);

/* a section = one big heading + a reading column; optional wide figure below (or right after the heading). tone = paper | white | sand | dark */
const Sec = ({ id, head, eyebrow, title, children, figure, figureFirst, testid, tone = "paper", className = "" }) => {
  const ref = useReveal();
  return (
    <section id={id} className={`kvl-sec kvl-tone-${tone} ${className}`} data-head={head || (title ? title.split("\n")[0] : "")} data-tone={tone} data-testid={testid} ref={ref}>
      <div className="kvl-col kvl-reveal">
        {eyebrow && (
          <p className="kvl-sec-eyebrow" data-testid={`${testid}-eyebrow`}>
            {eyebrow}
          </p>
        )}
        {title && <Lines text={title} className="kvl-h2" testid={`${testid}-title`} />}
        {!figureFirst && children}
      </div>
      {figure && <div className="kvl-reveal kvl-reveal-2">{figure}</div>}
      {figureFirst && <div className="kvl-col kvl-reveal kvl-reveal-2 kvl-after">{children}</div>}
    </section>
  );
};

const Paras = ({ items, className = "kvl-p" }) =>
  (Array.isArray(items) ? items : [items]).filter(Boolean).map((x) => (
    <p key={x} className={className}>
      {x}
    </p>
  ));

/* a prototype moment: wide, minimal frame, one caption */
const Figure = ({ children, caption, testid, className = "", style }) => (
  <figure className={`kvl-figure ${className}`} style={style} data-testid={testid}>
    <div className="kvl-frame">{children}</div>
    <figcaption>{caption}</figcaption>
  </figure>
);

/* the hero: the claim on the left – one line of context, the headline, one sentence – and a still of the
   prototype on the right (one opportunity), bleeding off the edge of the page. No buttons, no numbers, no pills. Stacks below 1100 px. */
const Hero = ({ d, date, wide }) => {
  const month = monthLabel(d, date);
  const h = d.hero;
  /* written like the first line of a letter, not a badge: «Tilbud til Mediability AS, oktober 2026» */
  const kicker = `${d.ui.proposalTo} ${d.to.company}, ${d.lang === "en" ? month : month.toLowerCase()} ${date.getFullYear()}`;
  return (
    <header id="cover" className="kvl-hero kvl-hero-v3" data-head={d.ui.proposal} data-testid="tilbod-cover">
      <div className="kvl-hero-split">
        <div className="kvl-hero-copy">
          <p className="kvl-hero-kicker kvl-rise" data-testid="tilbod-eyebrow">
            {kicker}
          </p>
          <Lines text={d.title} as="h1" className="kvl-h1 kvl-rise" testid="tilbod-title" />
          <p className="kvl-hero-sub kvl-rise" style={{ "--d": "0.1s" }} data-testid="tilbod-subtitle">
            {h.sub}
          </p>
        </div>
        {wide ? (
          <div className="kvl-hero-shot kvl-rise" style={{ "--d": "0.22s" }} data-testid="tilbod-hero-shot">
            <div className="kvl-hero-shot-in">
              <div className="kvl-frame kvl-hero-frame">
                <MbStill caption={d.ui.prototype} />
              </div>
            </div>
          </div>
        ) : (
          /* on a phone: the dashboard, as it looks on a phone, in a device-like card */
          <div className="kvl-hero-shot kvl-hero-shot-phone kvl-rise" style={{ "--d": "0.22s" }} data-testid="tilbod-hero-shot">
            <div className="kvl-hero-shot-in">
              <div className="kvl-phone-card">
                <PhoneStill />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

/* the letter: a greeting, the paragraphs, and the person who signs it */
const Letter = ({ d }) => {
  const ref = useReveal();
  return (
    <section id="letter" className="kvl-sec kvl-tone-white kvl-letter-sec" data-head={d.letter.greeting.replace(/,$/, "")} data-tone="white" data-testid="tilbod-letter" ref={ref}>
      <div className="kvl-col kvl-reveal">
        <p className="kvl-greeting" data-testid="tilbod-greeting">
          {d.letter.greeting}
        </p>
        <Paras items={d.letter.p} />
        <div className="kvl-sign" data-testid="tilbod-sign">
          <p className="kvl-p kvl-sign-close">{d.letter.close}</p>
          <div className="kvl-signer">
            <img src={d.from.photo} alt={d.from.person} className="kvl-signer-img" width="72" height="72" loading="lazy" data-testid="tilbod-signer-photo" />
            <div>
              <span className="kvl-signer-name">{d.from.person}</span>
              <span className="kvl-signer-role">{d.from.company}</span>
              <a className="kvl-signer-mail" href={`mailto:${d.from.email}`} data-testid="tilbod-signer-mail">
                {d.from.email}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Flow = ({ steps }) => (
  <ol className="kvl-col kvl-steps" data-testid="tilbod-flow-steps">
    {steps.map(([k, v], i) => (
      <li key={k} data-testid={`tilbod-flow-${i + 1}`}>
        <span className="kvl-step-n">{String(i + 1).padStart(2, "0")}</span>
        <h3 className="kvl-h3">{k}</h3>
        <p className="kvl-p">{v}</p>
      </li>
    ))}
  </ol>
);

/* the pricing section: three key figures, how an estimate becomes an offer, what the estimate rests on, the licence and the Visma boundary, the terms */
const Pricing = ({ d }) => {
  const pr = d.price;
  return (
    <div className="kvl-pricing" data-testid="tilbod-pricing">
      <div className="kvl-price-cards" data-testid="tilbod-price-cards">
        {pr.cards.map((c) => (
          <article key={c.key} className="kvl-price-card" data-testid={`tilbod-price-card-${c.key}`}>
            <span className="kvl-price-value">{c.value}</span>
            <span className="kvl-price-label">{c.label}</span>
            <p className="kvl-price-text">{c.text}</p>
          </article>
        ))}
      </div>

      {pr.steps && (
        <>
          <h3 className="kvl-h3 kvl-price-h">{pr.stepsTitle}</h3>
          <ol className="kvl-price-steps" data-testid="tilbod-price-steps">
            {pr.steps.map(([k, v], i) => (
              <li key={k} data-testid={`tilbod-price-step-${i + 1}`}>
                <span className="kvl-step4-n">{String(i + 1).padStart(2, "0")}</span>
                <h4 className="kvl-h4 kvl-step4-name">{k}</h4>
                <p>{v}</p>
              </li>
            ))}
          </ol>
        </>
      )}

      <div className="kvl-price-lower">
        {pr.basis && (
          <div data-testid="tilbod-price-basis">
            <h3 className="kvl-h4 kvl-price-sub-h">{pr.basisTitle}</h3>
            <dl className="kvl-price-basis">
              {pr.basis.map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
            </dl>
            {pr.basisNote && <p className="kvl-small kvl-price-note">{pr.basisNote}</p>}
          </div>
        )}
        <div>
          {pr.licence && (
            <div data-testid="tilbod-price-licence">
              <h3 className="kvl-h4 kvl-price-sub-h">{pr.licenceTitle}</h3>
              <Paras items={pr.licence} className="kvl-price-p" />
            </div>
          )}
          {pr.vismaNote && (
            <div className="kvl-price-visma" data-testid="tilbod-price-visma">
              <h3 className="kvl-h4 kvl-price-sub-h">{pr.vismaTitle}</h3>
              <p className="kvl-price-p">{pr.vismaNote}</p>
            </div>
          )}
        </div>
      </div>

      <div className="kvl-price-terms">
        <h3 className="kvl-h4 kvl-terms-h">{pr.termsTitle}</h3>
        <ul className="kvl-terms" data-testid="tilbod-terms">
          {pr.terms.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* who we are: the claim and the text on the left, the four of us on the right */
const About = ({ d }) => {
  const ab = d.about;
  const ref = useReveal();
  return (
    <section id="about" className="kvl-sec kvl-tone-paper kvl-about-sec" data-head={ab.title.split(" ").slice(0, 2).join(" ")} data-tone="paper" data-testid="tilbod-about" ref={ref}>
      <div className="kvl-wide kvl-about-grid">
        <div className="kvl-about-text kvl-reveal">
          <Lines text={ab.title} className="kvl-h2" testid="tilbod-about-title" />
          <div className="kvl-about">
            <Paras items={ab.p} />
          </div>
          {ab.checks && (
            <ul className="kvl-checks" data-testid="tilbod-about-checks">
              {ab.checks.map((x) => (
                <li key={x}>
                  <Check aria-hidden="true" /> {x}
                </li>
              ))}
            </ul>
          )}
          <p className="kvl-p kvl-refs" data-testid="tilbod-refs">
            <span className="kvl-mono">{ab.refsLabel}</span>
            {ab.refItems.map((c, i) => (
              <a key={c.id} href={c.url} target="_blank" rel="noreferrer" data-testid={`tilbod-ref-link-${i + 1}`}>
                {c.k} <span aria-hidden="true">↗</span>
              </a>
            ))}
            {ab.site && (
              <a href={ab.site.url} target="_blank" rel="noreferrer" className="kvl-refs-site" data-testid="tilbod-about-site">
                {ab.site.label} <span aria-hidden="true">↗</span>
              </a>
            )}
          </p>
        </div>
        <ul className="kvl-team kvl-reveal kvl-reveal-2" data-testid="tilbod-team">
          {ab.team.map((m, i) => (
            <li key={m.name} data-testid={`tilbod-team-${i + 1}`}>
              <img src={m.img} alt={m.name} width="600" height="660" loading="lazy" decoding="async" />
              <strong>{m.name}</strong>
              <span>{m.role}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/* the way forward: two calm paragraphs, three half-hours, who to talk to. No buttons. */
const Next = ({ d, valid }) => {
  const nx = d.next;
  return (
    <>
      <Paras items={nx.p} />
      {nx.agendaTitle && <p className="kvl-p kvl-agenda-h">{nx.agendaTitle}</p>}
      <ol className="kvl-agenda" data-testid="tilbod-next-agenda">
        {nx.agenda.map(([tm, what], i) => (
          <li key={what} data-testid={`tilbod-next-agenda-${i + 1}`}>
            <span className="kvl-mono">{tm}</span>
            <span>{what}</span>
          </li>
        ))}
      </ol>
      {nx.after && <p className="kvl-p kvl-agenda-after">{nx.after}</p>}
      <div className="kvl-closing" data-testid="tilbod-next-mail">
        <span className="kvl-closing-name">{d.from.person}</span>
        <a href={`mailto:${d.from.email}`}>{d.from.email}</a>
        <span className="kvl-mono kvl-meta" data-testid="tilbod-meta">
          {d.ref} · {d.ui.validUntil} {fmtDate(valid, d.lang)}
        </span>
      </div>
    </>
  );
};

/* the roadmap: four steps side by side, one line under each; then what is in the first delivery, as a grid */
const Roadmap = ({ dl }) => (
  <div className="kvl-wide kvl-roadmap" data-testid="tilbod-plan">
    <ol className="kvl-steps4">
      {dl.phases.map((ph, i) => {
        const [k, name, v] = ph.length === 3 ? ph : [ph[0], null, ph[1]];
        return (
          <li key={k} data-testid={`tilbod-plan-${i + 1}`}>
            <span className="kvl-step4-n">{String(i + 1).padStart(2, "0")}</span>
            <span className="kvl-mono kvl-step4-week">{k}</span>
            {name && <h3 className="kvl-h4 kvl-step4-name">{name}</h3>}
            <p>{v}</p>
          </li>
        );
      })}
    </ol>
    {dl.after && (
      <p className="kvl-plan-after" data-testid="tilbod-plan-after">
        {dl.after}
      </p>
    )}
    {dl.includedTitle && <h3 className="kvl-h3 kvl-included-h">{dl.includedTitle}</h3>}
    {dl.includedItems ? (
      <dl className="kvl-included-grid" data-testid="tilbod-included">
        {dl.includedItems.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    ) : (
      <p className="kvl-p kvl-included" data-testid="tilbod-included">
        {dl.included}
      </p>
    )}
    <p className="kvl-small kvl-outside">{dl.outside}</p>
  </div>
);

export const ProductProposal = ({ d }) => {
  useKeys(d.pages);
  useMeta(`${d.ui.proposal} · ${d.to.company} – ${d.from.company}`);
  const wide = useWide();
  const [liveOpen, setLiveOpen] = useState(false);
  const date = new Date();
  const valid = new Date(date.getTime() + d.validDays * 86400000);
  const { idea, flow, ai, visma, delivery: dl, price: pr } = d;

  return (
    <div className="kvp kvl" lang={d.lang} data-testid="tilbod-page">
      <a className="kvp-skip" href="#letter">
        {d.ui.skip}
      </a>
      <Top d={d} />
      <main>
        <Hero d={d} date={date} wide={wide} />
        <Letter d={d} />
        <QuoteShowcase d={d} />
        <LiveSystem d={d} open={liveOpen} onOpenChange={setLiveOpen} compact={!wide} />

        <Sec id="idea" eyebrow={idea.eyebrow} title={idea.title} testid="tilbod-idea" className="kvl-sec-center" figure={<SystemMockup d={d} />}>
          <Paras items={idea.p} />
        </Sec>

        <Sec id="flow" title={flow.title} head={flow.head} testid="tilbod-flow" tone="white" figure={<Flow steps={flow.steps} />} className={flow.title ? "" : "kvl-sec-notitle"} />

        <Sec
          id="ai"
          title={ai.title}
          testid="tilbod-ai"
          tone="sand"
          figure={
            <>
              <p className="kvl-col kvl-example" data-testid="tilbod-ai-example">
                <span>{ai.example.from}</span>
                <span className="kvl-arrow" aria-hidden="true">
                  →
                </span>
                <span>{ai.example.to}</span>
              </p>
              <Figure caption={ai.example.caption} testid="tilbod-ai-figure" className="kvl-figure-mock">
                <WorkspaceMockup d={d} />
              </Figure>
            </>
          }
        >
          <Paras items={ai.p} />
          {ai.list && (
            <ul className="kvl-list" data-testid="tilbod-ai-list">
              {ai.list.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
          {ai.after && <p className="kvl-p kvl-emph">{ai.after}</p>}
        </Sec>

        <Sec id="visma" title={visma.title} testid="tilbod-visma" tone="white">
          <Paras items={visma.p} />
          <div className="kvl-stack" data-testid="tilbod-visma-chain">
            {visma.line.map((x, i) => (
              <div key={x} className="kvl-stack-node" data-testid={`tilbod-visma-node-${i + 1}`}>
                {i > 0 && (
                  <span className="kvl-stack-arrow" aria-hidden="true">
                    ↓
                  </span>
                )}
                <span className="kvl-stack-title">{x}</span>
                <span className="kvl-stack-sub">{visma.under[i]}</span>
              </div>
            ))}
          </div>
          {visma.after && <p className="kvl-p">{visma.after}</p>}
          <p className="kvl-small">{visma.note}</p>
        </Sec>

        <Sec id="delivery" title={dl.title} testid="tilbod-delivery" figure={<Roadmap dl={dl} />}>
          <p className="kvl-p">{dl.p}</p>
        </Sec>

        <Sec id="price" title={pr.title} testid="tilbod-price" tone="sand" figure={<Pricing d={d} />}>
          <Paras items={pr.intro || pr.p} />
        </Sec>

        {d.about && <About d={d} />}

        <Sec id="next" title={d.next.title} testid="tilbod-next" tone="white" className="kvl-sec-tight">
          <Next d={d} valid={valid} />
        </Sec>

        <footer className="kvl-foot kvl-tone-white" data-testid="tilbod-footer">
          <div className="kvl-wide kvl-foot-in">
            <div className="kvl-foot-brand">
              <a href="https://kviteberg.no" target="_blank" rel="noreferrer" className="kvl-foot-logo" aria-label="Kviteberg" data-testid="tilbod-footer-logo">
                <KvitebergLogo className="kvl-logo-svg" />
              </a>
              <span>
                {d.from.company} · {d.from.address}
              </span>
            </div>
            <span>
              {d.ref} · {d.ui.confidential}
            </span>
          </div>
        </footer>
      </main>
    </div>
  );
};
